import React, { type HTMLAttributes } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import {
  Accordion, AccordionItem, Container, WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { DynamicMediaItem } from '@uncinc/react-drupal-core';

import type { NodeFAQ } from '../../../types/uncinc_profile/nodes';
import type { ParagraphFAQ as Type } from '../../../types/uncinc_profile/paragraphs';
import { ButtonWrapper } from '../../../components';
import { DEFAULT_LANGUAGE } from '../../../config/constants';

import './style.scss';

interface ParagraphFAQProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const ParagraphFAQ: React.FC<ParagraphFAQProps> = ({ id, entity }) => {
  const paragraphFAQ = entity.toJS();
  const title = paragraphFAQ.field_title[0]?.value;
  const body = paragraphFAQ.field_body[0]?.processed;
  const faqItems = paragraphFAQ.field_faq;

  const currentEntityId = useSelector<Immutable.Map<any, any>>((state) => state.getIn(['entity', 'entityId'], null));
  // @ts-ignore
  const currentEntity: ImmutableEntity<NodeFAQ> = useSelector<Immutable.Map<any, any>>(
    (state) => state.getIn(['node', currentEntityId]),
  );
  const langcode = currentEntity.getIn(['langcode', 0, 'value'], DEFAULT_LANGUAGE);
  const path = currentEntity.getIn(['paths', langcode], '/');


  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': path,
    mainEntity: faqItems.map((faqItem) => ({
      '@type': 'Question',
      name: faqItem.entity.title[0].value,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqItem.entity.body[0].processed,
      },
    })),
  });

  const horizontal = paragraphFAQ.field_horizontal?.[0]?.value;
  const bodyClass = classNames({
    'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2': horizontal === false,
    'col-12 col-md-10 offset-md-1 col-lg-4 offset-lg-0': horizontal,
  });
  const faqClass = classNames({
    'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2': horizontal === false,
    'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-0': horizontal,
  });

  const accordionItems = faqItems
    .map((faqItem) => {
      const faqTitle = faqItem.entity.title[0].value;
      const faqKey = faqItem.target_id;
      const faqBody = faqItem.entity.body[0].processed;
      const faqMedia = faqItem.entity.field_media_item?.[0];
      const faqMediaDescription = faqMedia?.entity.field_caption[0]?.value;
      const faqLink = faqItem.entity.field_link?.[0]?.uri;
      const faqLinkTitle = faqItem.entity.field_link?.[0]?.title;

      return (
        <AccordionItem
          key={faqKey}
          target_id={faqKey}
          title={faqTitle}
          className="ParagraphFAQ__accordion__item"
          materialIconName="keyboard_arrow_up"
          iconClassName="toggleIcon"
          content={(
            <>
              <WysiwygField html={faqBody} />
              {(faqLink || faqMedia) && (
                <div className="Accordion__item__wrapper">

                  {faqLink && (
                    <ButtonWrapper
                      type="textual"
                      href={faqLink}
                    >
                      {faqLinkTitle}
                    </ButtonWrapper>
                  )}

                  {faqMedia && (
                    <DynamicMediaItem
                      media={faqMedia}
                      imageAspectRatio="4_3"
                      className="Accordion__item__media"
                      caption={faqMediaDescription}
                    />
                  )}
                </div>
              )}
            </>
          )}
        />
      );
    });

  return (
    <>
      <script type="application/ld+json">
        {schema}
      </script>
      <div id={id} className="Paragraph ParagraphFAQ">
        <Container>
          <div className="row">
            {(title || body) && (
              <div className={bodyClass}>
                {title && (
                  <h2 className="ParagraphFAQ__title">{title}</h2>
                )}
                {body && (
                  <WysiwygField
                    className="ParagraphFAQ__body"
                    property="schema:text"
                    html={body}
                  />
                )}
              </div>
            )}
            <div className={faqClass}>
              <Accordion className="ParagraphFAQ__accordion">
                {accordionItems}
              </Accordion>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};
export default ParagraphFAQ;
